import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useSettings } from '../contexts/SettingsContext';

// Base navigation links
const baseNavLinks = [
  { title: 'Home', path: '/', key: 'home' },
  { title: 'About', path: '/about', key: 'about' },
  { title: 'Services', path: '/services', key: 'services' },
  { title: 'Portfolio', path: '/portfolio', key: 'portfolio' },
  { title: 'Tools', path: '/tools', key: 'tools' },
  { title: 'Rental', path: '/rental', key: 'rental' },
  { title: 'Contact', path: '/contact', key: 'contact' },
];

const Navbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { settings, loading } = useSettings();

  // Debug: Log settings to console
  useEffect(() => {
    if (!loading) {
      console.log('Navbar settings loaded:', settings);
      console.log('Logo URL from settings:', settings.general.logoUrl);
    }
  }, [settings, loading]);

  // Filter navigation links based on visibility settings
  const navLinks = baseNavLinks.filter(link => {
    // Home is always visible
    if (link.key === 'home') return true;
    
    // Check visibility for other pages
    return settings.pageVisibility[link.key as keyof typeof settings.pageVisibility];
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box 
      sx={{ 
        width: 250, 
        height: '100%', 
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column'
      }}
      role="presentation"
      onClick={handleDrawerToggle}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton color="inherit" onClick={handleDrawerToggle} edge="start">
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ flexGrow: 1 }}>
        {navLinks.map((link) => (
          <ListItem 
            component={Link} 
            to={link.path} 
            key={link.title}
            sx={{ 
              color: isActive(link.path) ? 'primary.main' : 'text.primary',
              borderLeft: isActive(link.path) ? '4px solid' : '4px solid transparent',
              borderColor: 'primary.main',
              '&:hover': {
                bgcolor: 'background.paper',
                color: 'primary.main',
              }
            }}
          >
            <ListItemText 
              primary={link.title} 
              primaryTypographyProps={{ 
                fontWeight: isActive(link.path) ? 'bold' : 'normal' 
              }} 
            />
          </ListItem>
        ))}
      </List>
      {settings.pageVisibility.contact && (
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            variant="contained" 
            color="primary" 
            fullWidth
            component={Link}
            to="/contact"
          >
            Get a Quote
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: 'rgba(18, 18, 18, 0.8)', backdropFilter: 'blur(8px)' }}>
        <Toolbar>
          {/* Logo and Company Name */}
          <Box 
            component={Link} 
            to="/" 
            sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none'
            }}
          >
            {settings.general.logoUrl && settings.general.logoUrl.trim() !== '' && (
              <Box
                component="img"
                src={settings.general.logoUrl}
                alt="Paragon Live Design Logo"
                sx={{ 
                  height: { xs: 32, sm: 40 }, 
                  mr: 1,
                  display: 'block'
                }}
                onError={(e) => {
                  console.error('Logo failed to load:', e);
                  // Hide the image element on error
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: 'white',
                letterSpacing: '0.05em',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              PARAGON LIVE DESIGN
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {navLinks.map((link) => (
                <Button 
                  key={link.title}
                  component={Link}
                  to={link.path}
                  color="inherit"
                  sx={{ 
                    mx: 1,
                    color: isActive(link.path) ? 'primary.main' : 'text.primary',
                    fontWeight: isActive(link.path) ? 'bold' : 'normal',
                    '&:hover': {
                      color: 'primary.main',
                    }
                  }}
                >
                  {link.title}
                </Button>
              ))}
              {settings.pageVisibility.contact && (
                <Button 
                  variant="contained" 
                  color="primary"
                  component={Link}
                  to="/contact"
                  sx={{ ml: 2 }}
                >
                  Get a Quote
                </Button>
              )}
            </Box>
          )}

          {/* Mobile Navigation Toggle */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
      >
        {drawer}
      </Drawer>

      {/* Toolbar spacer to prevent content from hiding behind AppBar */}
      <Toolbar />
    </>
  );
};

export default Navbar; 